import React, { useContext, useEffect, useState } from 'react';
import {
	Button,
	CheckboxField,
	SearchField,
	SelectFieldFormik,
	TextFieldFormik,
} from '@components';
import { ModalContext } from '@context';
import { AddressService } from '@data';
import { Address, AddressFormValues, AddressSearchFormValues, AddressType } from '@types';
import {
	FormLabels,
	getAddressByType,
	getAddressSearchInitialValues,
	isDirtyFormOnTrim,
	isEmptyAddress,
	isInAustralia,
	mapAddress,
	mapAddressToFormValues,
} from '@utils';
import countries from '@utils/sampleData/addresses/countries.json';
import states from '@utils/sampleData/addresses/states.json';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import styles from './addressDetails.module.scss';

const addressSearchFormLabels: FormLabels<AddressSearchFormValues> = {
	address: 'Address finder',
	isOverwrittingPostalAddress: 'Use as postal address',
};

const addressManualFormLabels: FormLabels<AddressFormValues> = {
	addressLine1: 'Address 1',
	addressLine2: 'Address 2',
	suburb: 'Suburb/Town',
	postalCode: 'Postcode/ZIP',
	state: 'State',
	country: 'Country',
	isOverwrittingPostalAddress: 'Use as postal address',
};

const addressSearchFormValidation = Yup.object().shape({
	address: Yup.object()
		.shape({
			postalCode: Yup.string().required('Address is required'),
		})
		.required(),
	isOverwrittingPostalAddress: Yup.boolean().nullable().optional(),
});

const addressManualFormValidation = Yup.object({
	addressLine1: Yup.string().required('Address 1 is required'),
	addressLine2: Yup.string().nullable().optional(),
	suburb: Yup.string().required('Suburb/Town is required'),
	postalCode: Yup.string().required('Postcode/Zip is required'),
	country: Yup.string().required('Country is required'),
	state: Yup.string().when('country', {
		is: (val: string) => val?.toLowerCase() == 'australia',
		then: schema => schema.required('State is required'),
		otherwise: schema => schema.nullable().optional(),
	}),
	isOverwrittingPostalAddress: Yup.boolean().nullable().optional(),
});

export const AddressDetails = () => {
	// Context
	const {
		addressTypeModal: addressType,
		streetAddress,
		postalAddress,
		setShowAddressModal,
		setAddressFormValuesOnSave,
	} = useContext(ModalContext);

	// State
	const [isManualAddress, setIsManualAddress] = useState(false);
	const [addressSearchInitialValues] = useState<AddressSearchFormValues>(
		getAddressSearchInitialValues(addressType, streetAddress, postalAddress),
	);
	const [addressManualInitialValues] = useState<AddressFormValues>(
		mapAddressToFormValues(getAddressByType(addressType, streetAddress, postalAddress)),
	);

	const cancel = () => {
		setShowAddressModal(false);
	};

	const save = (values: AddressSearchFormValues) => {
		setShowAddressModal(false);
		setAddressFormValuesOnSave(values);
	};

	const saveManualAddress = (addressValues: AddressFormValues) => {
		const { isOverwrittingPostalAddress, ...addressValue } = addressValues;
		save({
			address: addressValue,
			isOverwrittingPostalAddress: isOverwrittingPostalAddress,
		});
	};

	const onSelectRawAddress = async (
		rawAddress: string,
		setFieldValue: (field: string, value: Address, shouldValidate?: boolean | undefined) => void,
	) => {
		const finalAddress = await AddressService.parseToValidAddress(rawAddress);
		setFieldValue('address', finalAddress);
	};

	// Life cycle
	useEffect(() => {
		if (isManualAddress) {
			addressManualFormLabels.suburb = 'Suburb/Town';
			addressManualFormLabels.postalCode = 'Postcode/ZIP';
		}
	}, [isManualAddress]);

	const AddressSearchForm = () => (
		<Formik
			initialValues={addressSearchInitialValues}
			validationSchema={addressSearchFormValidation}
			onSubmit={save}
		>
			{({
				dirty,
				submitForm,
				values,
				errors,
				touched,
				setFieldValue,
			}: FormikProps<AddressSearchFormValues>) => (
				<Form>
					<div className={['form--grid', 'form--grid--double'].join(' ')}>
						<>
							<label>Address finder:</label>
							<SearchField
								defaultValue={mapAddress(values.address)}
								searchFunction={async value => await AddressService.search(value)}
								handleChange={async selectedRawAddress =>
									onSelectRawAddress(selectedRawAddress, setFieldValue)
								}
								// eslint-disable-next-line
								// @ts-ignore // Formik detects type as string instead of AddressType
								hasError={Boolean(errors.address?.postalCode && touched.address?.postalCode)}
								// eslint-disable-next-line
								// @ts-ignore // Formik detects type as string instead of AddressType
								errorMsg={errors.address?.postalCode}
								placeholder=""
							/>
						</>
						{addressType == AddressType.STREET ? (
							<>
								<label></label>
								<CheckboxField
									value={values.isOverwrittingPostalAddress}
									onChange={(value: boolean) => setFieldValue('isOverwrittingPostalAddress', value)}
									isLabelClickable={true}
									customStyles={[styles.checkbox, styles.centered].join(' ')}
								>
									{addressSearchFormLabels.isOverwrittingPostalAddress}
								</CheckboxField>
							</>
						) : (
							<></>
						)}
					</div>
					<div className={styles.gap}></div>
					<div className={styles['button-aligned-container']}>
						<Button
							type="primary"
							size="large"
							onClick={() => setIsManualAddress(true)}
							customStyles={styles.centered}
						>
							Enter address manually
						</Button>
					</div>
					<Buttons isDirty={dirty} submitForm={submitForm} />
				</Form>
			)}
		</Formik>
	);

	const AddressManualForm = () => (
		<>
			<Formik
				initialValues={addressManualInitialValues}
				validationSchema={addressManualFormValidation}
				onSubmit={saveManualAddress}
			>
				{({ dirty, submitForm, values, getFieldHelpers }: FormikProps<AddressFormValues>) => {
					return (
						<Form>
							<div className={['form--grid', 'form--grid--double'].join(' ')}>
								<>
									<label>{addressManualFormLabels.country}:</label>
									<SelectFieldFormik
										props={{
											name: 'country',
										}}
										size="small"
										placeholder=""
										data={countries}
										handleChange={() => {
											// reset dependant state field
											getFieldHelpers('state').setValue('');
											getFieldHelpers('state').setTouched(false, false);
										}}
									/>
								</>
								<>
									<label>{addressManualFormLabels.addressLine1}:</label>
									<TextFieldFormik
										props={{
											name: 'addressLine1',
										}}
										size="auto"
									/>
								</>
								<>
									<label>{addressManualFormLabels.addressLine2}:</label>
									<TextFieldFormik
										props={{
											name: 'addressLine2',
											placeholder: '(Optional)',
										}}
										size="auto"
									/>
								</>
								<>
									<label>{addressManualFormLabels.suburb}:</label>
									<TextFieldFormik
										props={{
											name: 'suburb',
										}}
										size="small"
									/>
								</>
								<>
									<label>{addressManualFormLabels.postalCode}:</label>
									<TextFieldFormik
										props={{
											name: 'postalCode',
										}}
										size="tiny"
									/>
								</>
								{isInAustralia(values) ? (
									<>
										<label>{addressManualFormLabels.state}:</label>
										<SelectFieldFormik
											props={{
												name: 'state',
											}}
											size="tiny"
											placeholder=""
											data={states}
										/>
									</>
								) : (
									<>
										<label>{addressManualFormLabels.state}:</label>
										<TextFieldFormik
											props={{
												name: 'state',
												placeholder: '(Optional)',
											}}
											size="tiny"
										/>
									</>
								)}
								{addressType == AddressType.STREET ? (
									<>
										<label></label>
										<CheckboxField
											value={values.isOverwrittingPostalAddress}
											onChange={(value: boolean) =>
												getFieldHelpers('isOverwrittingPostalAddress').setValue(value)
											}
											isLabelClickable={true}
											customStyles={[styles.checkbox, styles.centered].join(' ')}
										>
											{addressSearchFormLabels.isOverwrittingPostalAddress}
										</CheckboxField>
									</>
								) : (
									<></>
								)}
							</div>
							<Buttons
								isDirty={isDirtyFormOnTrim(dirty, addressManualInitialValues, values)}
								submitForm={submitForm}
							/>
						</Form>
					);
				}}
			</Formik>
		</>
	);

	const Buttons = ({
		isDirty,
		submitForm,
	}: {
		isDirty: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		submitForm: (() => Promise<void>) & (() => Promise<any>);
	}) => (
		<div className={styles['buttons-container']}>
			<Button onClick={cancel} size="small" type="secondary">
				Cancel
			</Button>
			<Button onClick={submitForm} size="small" type="primary" disabled={!isDirty}>
				Save
			</Button>
		</div>
	);

	return (
		<div className={styles.content}>
			{!isManualAddress ? (
				<>
					<h3>Update address details</h3>
					<p className={styles.instruction}>
						Please start typing your address and select a matching address or enter an address
						manually.
					</p>
					<AddressSearchForm />
				</>
			) : (
				<>
					<h3>
						{isEmptyAddress(addressType == AddressType.STREET ? streetAddress : postalAddress)
							? 'Add'
							: 'Edit'}{' '}
						address details
					</h3>
					<p className={styles.instruction}>Please enter your address details.</p>
					<AddressManualForm />
				</>
			)}
		</div>
	);
};
